import type { MouseEventHandler } from 'react'
import { memo, useCallback, useMemo } from 'react'
import { Marker } from 'react-map-gl/mapbox'
import useToggle from 'hooks/useToggle'
import styled from 'styled-components'
import type { PinSizing } from './Pins'
import ExperiencePins from './Pins'
import config from 'constants/config'

function scaleDownSizing(sizing: PinSizing): PinSizing {
  const SCALE = 5 / 6
  const { size, anchor } = sizing
  return {
    size: [size[0] * SCALE, size[1] * SCALE],
    anchor: [anchor[0] * SCALE, anchor[1] * SCALE],
  }
}

const MAX_PIN_LIMIT = config.search.mapView.pinLimit

const MarkerButton = styled.button`
  display: block;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
`

const MarkerImage = styled.img`
  display: block;
  max-width: none;
`

interface Props {
  marker: App.SearchExperienceMapMarker
  onClick: (marker: App.SearchExperienceMapMarker) => void
  scaleDown?: boolean
  active?: boolean
  visited?: boolean
  isHovered?: boolean
  index?: number
}

function SearchMapMarker(props: Props) {
  const {
    active,
    visited,
    isHovered,
    marker,
    scaleDown,
    onClick,
    index = 0,
  } = props

  const { value: hovered, on: hoverOn, off: hoverOff } = useToggle()

  const calculateZIndex = useCallback(
    (marker: App.SearchExperienceMapMarker, index: number) => {
      if (active) {
        return MAX_PIN_LIMIT + 1
      }
      if (isHovered || hovered) {
        return MAX_PIN_LIMIT
      }
      if (index >= MAX_PIN_LIMIT) {
        return 0
      }
      return MAX_PIN_LIMIT - index
    },
    [active, isHovered, hovered],
  )

  // Get the first category from all the experienceItems on that marker or 999 as a fallback pin
  const PinConfig = useMemo(() => {
    const pinConfigId = marker.experienceItems?.map(item => item.primaryCategory?.parentId)[0] || 999
    // sometimes pinConfigId is undefined, so we use 999 as a fallback
    // but we need to make sure that the pinConfigId is a valid key in the ExperiencePins object, if not we use 999 as a second fallback
    const pinConfig = ExperiencePins[pinConfigId] || ExperiencePins[999]
    return pinConfig
  }, [marker.experienceItems])

  let pinVersion = PinConfig.default
  if (active) {
    pinVersion = PinConfig.active
  } else if (visited) {
    pinVersion = PinConfig.previewed
  }

  const pin = (hovered || isHovered) ? pinVersion.hover : pinVersion.default
  const { image, sizing: fullSizing } = pin

  const sizing = scaleDown ? scaleDownSizing(fullSizing) : fullSizing

  const zIndex = useMemo(() => calculateZIndex(marker, index), [calculateZIndex, index, marker])
  const coordinate = useMemo(() => ({ lat: marker.latitude, lng: marker.longitude }), [marker.latitude, marker.longitude])
  const offset = useMemo<[number, number]>(() => ([
    -sizing.anchor[0],
    -sizing.anchor[1],
  ]), [sizing.anchor])

  const clickHandler = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    onClick(marker)
  }, [onClick, marker])

  return (
    <Marker
      longitude={coordinate.lng}
      latitude={coordinate.lat}
      anchor="top-left"
      offset={offset}
      style={{ zIndex }}
    >
      <MarkerButton
        type="button"
        onMouseEnter={hoverOn}
        onMouseLeave={hoverOff}
        onClick={clickHandler}
        aria-label={marker.experienceItems?.[0]?.name ?? marker.id}
      >
        <MarkerImage
          src={image}
          alt=""
          width={sizing.size[0]}
          height={sizing.size[1]}
        />
      </MarkerButton>
    </Marker>
  )
}

export default memo(SearchMapMarker)
